/**
 * Daily Scrum grouping — pure, deterministic roll-up of task updates into the
 * standup view: engineer → task → their LATEST update (Completed / Next /
 * Status, plus a blocked flag).
 *
 * One {@link TaskUpdate} document powers BOTH the task's own updates section and
 * this scrum roll-up (no duplicate write): the scrum simply projects the room's
 * updates, keeping only the newest update per (engineer, task) pair.
 *
 * Pure TS — no React, no I/O.
 */

import type { TaskUpdate, UpdateStatus } from '../models/types';

/** One task line under an engineer in the scrum. */
export interface ScrumTaskEntry {
  taskId: string;
  /** Resolved task title (falls back to the id when unresolved). */
  taskTitle: string;
  /** What was completed since the last update. */
  completed: string;
  /** What is planned next. */
  next: string;
  /** The rolled-up standup status, when set. */
  status?: UpdateStatus;
  /** Free-text blocker note from the update, when present. */
  blocker?: string;
  /** True when the update (or the task) marks this work blocked. */
  blocked: boolean;
  /** ISO-8601 timestamp of the latest update. */
  updatedAt: string;
}

/** All of an engineer's scrum lines, newest task first. */
export interface ScrumEngineerGroup {
  engineer: string;
  entries: ScrumTaskEntry[];
}

/**
 * Groups a room's task updates into the Daily Scrum view. For each
 * (engineer, task) pair only the newest update is kept. Engineers are sorted
 * alphabetically; within an engineer, task lines are sorted newest-update-first.
 *
 * @param updates      the room's task updates (any order)
 * @param taskTitles   taskId → title for display
 * @param blockedTasks optional set of taskIds currently marked blocked on the
 *                     task itself (so a blocked task shows ⚠ even if the latest
 *                     update didn't restate the blocker)
 */
export function groupUpdatesByEngineer(
  updates: TaskUpdate[],
  taskTitles: Map<string, string>,
  blockedTasks?: Set<string>,
): ScrumEngineerGroup[] {
  // newest per (engineer|task)
  const latest = new Map<string, TaskUpdate>();
  for (const u of updates ?? []) {
    if (!u.author || !u.taskId) continue;
    const key = `${u.author}\u0000${u.taskId}`;
    const prev = latest.get(key);
    if (!prev || (u.createdAt ?? '') > (prev.createdAt ?? '')) {
      latest.set(key, u);
    }
  }

  const byEngineer = new Map<string, ScrumTaskEntry[]>();
  for (const u of latest.values()) {
    const entry: ScrumTaskEntry = {
      taskId: u.taskId,
      taskTitle: taskTitles.get(u.taskId) || u.taskId,
      completed: u.completed ?? '',
      next: u.next ?? '',
      status: u.status,
      blocker: u.blocker,
      blocked:
        u.status === 'blocked' ||
        !!u.blocker ||
        (blockedTasks?.has(u.taskId) ?? false),
      updatedAt: u.createdAt ?? '',
    };
    const list = byEngineer.get(u.author) ?? [];
    list.push(entry);
    byEngineer.set(u.author, list);
  }

  return Array.from(byEngineer.entries())
    .map(([engineer, entries]) => ({
      engineer,
      entries: entries.sort((a, b) =>
        (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''),
      ),
    }))
    .sort((a, b) => a.engineer.localeCompare(b.engineer));
}
